// Sourcing events list (Lane 1322). Shown only when /me says
// sourcing_events_enabled; every API route also answers 404 when it is off.
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { PageShell, TableShell } from '../../components/ui/DataPrimitives';
import ScopeBuilder, { emptyFilters, toServerFilters } from './ScopeBuilder';
import { useSourcingApi, formatDate } from './sourcingApi';
import DeleteEventConfirm, { canDelete } from './DeleteEventConfirm';
import '../ListLinear.css';
import './SourcingEvents.css';

// Lane 1368 (S6): what "Start in chat" types in the Home composer (not sent).
export const START_IN_CHAT_TEXT = 'I want to set up an RFP for ';

const STATUS_TEXT = {
  draft: 'Draft', open: 'Open for bids', closed: 'Bidding closed', awarded: 'Awarded', cancelled: 'Cancelled',
};

export default function SourcingEvents() {
  const navigate = useNavigate();
  const location = useLocation();
  const api = useSourcingApi();
  // Lane 1368 (S6): with the chat intake on, "New event" asks how to start.
  const { sourcingChatIntakeEnabled } = useAuth();
  const [choosing, setChoosing] = useState(false);
  const [events, setEvents] = useState(null);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [options, setOptions] = useState(null);
  const [name, setName] = useState('');
  const [filters, setFilters] = useState(emptyFilters());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  // Lane 1433: the event being confirmed for delete, and the line shown after one.
  const [deleting, setDeleting] = useState(null);
  const [deletedNote, setDeletedNote] = useState(
    (location.state && location.state.deletedEventName) ? `Deleted ${location.state.deletedEventName}.` : '');

  const load = useCallback(() => {
    api.listEvents().then((r) => { setEvents(r.items || []); setError(''); })
      .catch((e) => setError(e.message));
  }, [api]);

  useEffect(() => { load(); }, [load]);
  // The "Deleted ..." line from the event page shows once, not again on a reload.
  useEffect(() => {
    if (location.state && location.state.deletedEventName) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);
  useEffect(() => {
    if (creating && !options) api.filterOptions().then(setOptions).catch(() => setOptions({ categories: [], sites: [] }));
  }, [creating, options, api]);

  const preview = useCallback((f) => api.previewScope(f), [api]);
  const hasFilters = filters.level_1 || filters.level_2 || filters.sites.length || filters.supplier;

  const create = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const r = await api.createEvent(name.trim(), hasFilters ? toServerFilters(filters) : undefined);
      navigate(`/sourcing/${r.event_public_id}`);
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageShell className="ll">
      <div className="ll-wrap">
        <header className="ll-head">
          <div>
            <h1 className="ll-h1">Sourcing events</h1>
            <div className="ll-sub">Put parts out for bid, compare offers and choose who to award</div>
          </div>
          {sourcingChatIntakeEnabled ? (
            <button type="button" className="srce-btn srce-btn--primary srce-push"
              onClick={() => { if (creating || choosing) { setCreating(false); setChoosing(false); } else setChoosing(true); }}>
              {creating || choosing ? 'Close' : 'New event'}
            </button>
          ) : (
            <button type="button" className="srce-btn srce-btn--primary srce-push" onClick={() => setCreating((v) => !v)}>
              {creating ? 'Close' : 'New event'}
            </button>
          )}
        </header>

        {sourcingChatIntakeEnabled && choosing && !creating && (
          <section className="srce-panel" aria-label="New event" data-testid="srce-new-choice">
            <div className="srce-strong">How do you want to start?</div>
            <div className="srce-actions">
              <button type="button" className="srce-btn srce-btn--primary"
                onClick={() => navigate('/', { state: { composerDraft: START_IN_CHAT_TEXT } })}>
                Start in chat
              </button>
              <button type="button" className="srce-btn" onClick={() => { setChoosing(false); setCreating(true); }}>
                Manual build
              </button>
            </div>
            <div className="srce-mut">
              In chat, Wingman asks for the parts, your goal, the suppliers and the dates, then shows a
              summary for you to confirm. Nothing is sent to any supplier.
            </div>
          </section>
        )}

        {creating && (
          <section className="srce-panel" aria-label="New event">
            <div className="srce-field">
              <label className="srce-label" htmlFor="srce-name">Event name</label>
              <input id="srce-name" className="srce-input srce-input--wide" value={name} maxLength={255}
                placeholder="For example: O-rings at Lancaster Plant" onChange={(e) => setName(e.target.value)} />
            </div>
            <ScopeBuilder api={api} options={options} filters={filters} onChange={setFilters} preview={preview} />
            <div className="srce-actions">
              <button type="button" className="srce-btn srce-btn--primary" disabled={!name.trim() || saving} onClick={create}>
                {saving ? 'Creating…' : hasFilters ? 'Create event with these parts' : 'Create an empty event'}
              </button>
              {saveError && <span className="srce-crit">{saveError}</span>}
            </div>
          </section>
        )}

        {deletedNote && <div className="srce-notice" role="status">{deletedNote}</div>}
        {deleting && (
          <DeleteEventConfirm api={api} event={deleting} onCancel={() => setDeleting(null)}
            onDeleted={(e) => {
              // The server confirmed: the row leaves at once, then the list reloads.
              setEvents((rows) => (rows || []).filter((x) => x.public_id !== e.public_id));
              setDeleting(null);
              setDeletedNote(`Deleted ${e.name}.`);
              load();
            }} />
        )}
        {error && <div className="ll-empty"><div className="ll-empty-t">Unable to load events</div>{error}</div>}
        {!error && events === null && <div className="ll-loading">Loading events…</div>}
        {!error && events && events.length === 0 && !creating && (
          <div className="ll-empty">
            <div className="ll-empty-t">No sourcing events yet</div>
            Start one here, or ask Wingman in chat to bid out a group of parts.
          </div>
        )}
        {!error && events && events.length > 0 && (
          <TableShell className="srce-tbl srce-tbl--events" aria-label="Sourcing events">
            <div className="srce-tr srce-th">
              <div>Event</div><div>Status</div><div className="srce-r">Lines</div>
              <div className="srce-r">Suppliers</div><div className="srce-r">Updated</div>
              <div><span className="srce-sr">Actions</span></div>
            </div>
            {events.map((e) => (
              <div key={e.public_id} className="srce-tr srce-link" tabIndex={0}
                onClick={() => navigate(`/sourcing/${e.public_id}`)}
                onKeyDown={(k) => { if (k.key === 'Enter') navigate(`/sourcing/${e.public_id}`); }}>
                <div className="srce-strong">{e.name}</div>
                <div><span className={`srce-status srce-status--${e.status}`}>{STATUS_TEXT[e.status] || e.status}</span></div>
                <div className="srce-r">{Number(e.included_lines).toLocaleString()}</div>
                <div className="srce-r">{e.suppliers}</div>
                <div className="srce-r srce-mut">{formatDate(e.updated_at)}</div>
                <div className="srce-r">
                  {canDelete(e) && (
                    <button type="button" className="srce-btn srce-btn--sm srce-btn--neg"
                      aria-label={`Delete ${e.name}`}
                      onClick={(k) => { k.stopPropagation(); setDeletedNote(''); setDeleting(e); }}
                      onKeyDown={(k) => k.stopPropagation()}>
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </TableShell>
        )}
      </div>
    </PageShell>
  );
}
